import { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Toolbar,
  CssBaseline,
  IconButton,
  AppBar,
  Typography,
  ListItemButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import LogoutIcon from "@mui/icons-material/Logout";
import SchoolIcon from "@mui/icons-material/School";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import ShareIcon from "@mui/icons-material/Share";
import CampaignIcon from "@mui/icons-material/Campaign";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import LinkIcon from "@mui/icons-material/Link";
import InsightsIcon from "@mui/icons-material/Insights";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import logo from "../assets/logo.png";
import { useThemeMode } from "../theme";
import {
  Link,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { DashboardSection } from "./dashboard";
import { UserSection } from "./users";
import { UserTreeSection } from "./user-tree";
import { CoursesSection } from "./courses";
import { CourseSeasonsSection } from "./course-seasons";
import { CourseItemsSection } from "./course-items";
import { BDSection } from "./bd";
import { BDDetailsSection } from "./bd-details";
import { ReferralsSection } from "./referrals";
import { ReferralDetailsSection } from "./referral-details";
import { MiniAppNotificationsSection } from "./miniapp-notifications";
import { LiveStreamSection } from "./live-stream";
import { BotNotificationsSection } from "./bot-notifications";
import { CourseLinkGeneratorSection } from "./course-link-generator";
import { CampaignStatsSection } from "./campaign-stats";
import { getToken, removeToken } from "../utils";
import { history } from "../utils/axios-instance";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"; // Import React Query
import { MenuIcon as IconWrapper } from "../components";

const drawerWidth = 240;

const queryClient = new QueryClient(); // Initialize React Query Client

export default function Home() {
  const navigate = useNavigate();
  if (!getToken()) navigate("/login");
  const location = useLocation();
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Single navy accent for every nav icon keeps the sidebar a clean two-tone (navy + white) surface.
  const navyIconColor = theme.palette.primary.main;
  const navyIconBg = alpha(theme.palette.primary.main, 0.1);

  const menuItems = [
    {
      text: "Dashboard",
      icon: <IconWrapper icon={<HomeIcon />} color={navyIconColor} bgColor={navyIconBg} />,
      path: "/",
    },
    {
      text: "Users",
      icon: <IconWrapper icon={<PeopleIcon />} color={navyIconColor} bgColor={navyIconBg} />,
      path: "/user",
    },
    {
      text: "BD Program",
      icon: <IconWrapper icon={<BusinessCenterIcon />} color={navyIconColor} bgColor={navyIconBg} />,
      path: "/bd",
    },
    {
      text: "Referrals",
      icon: <IconWrapper icon={<ShareIcon />} color={navyIconColor} bgColor={navyIconBg} />,
      path: "/referrals",
    },
    {
      text: "Campaign Stats",
      icon: <IconWrapper icon={<InsightsIcon />} color={navyIconColor} bgColor={navyIconBg} />,
      path: "/campaign-stats",
    },
    {
      text: "Live Stream",
      icon: <IconWrapper icon={<LiveTvIcon />} color={navyIconColor} bgColor={navyIconBg} />,
      path: "/live-stream",
    },
    {
      text: "Courses",
      icon: <IconWrapper icon={<SchoolIcon />} color={navyIconColor} bgColor={navyIconBg} />,
      path: "/courses",
    },
    {
      text: "Course Links",
      icon: <IconWrapper icon={<LinkIcon />} color={navyIconColor} bgColor={navyIconBg} />,
      path: "/course-links",
    },
    {
      text: "MiniApp Notifications",
      icon: <IconWrapper icon={<CampaignIcon />} color={navyIconColor} bgColor={navyIconBg} />,
      path: "/miniapp-notifications",
    },
    {
      text: "Bot Notifications",
      icon: <IconWrapper icon={<SmartToyIcon />} color={navyIconColor} bgColor={navyIconBg} />,
      path: "/bot-notifications",
    },
  ];

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {!isMobile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2.5,
            py: 2.5,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Amoozical"
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              flexShrink: 0,
              objectFit: "cover",
            }}
          />
          <Box sx={{ overflow: "hidden", flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              Amoozical
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Backoffice
            </Typography>
          </Box>
          <Tooltip title={mode === "dark" ? "Switch to light mode" : "Switch to night mode"}>
            <IconButton onClick={toggleMode} size="small">
              {mode === "dark" ? (
                <LightModeOutlinedIcon fontSize="small" />
              ) : (
                <DarkModeOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {isMobile && <Toolbar />}
      <List sx={{ px: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.14),
                  },
                },
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
                borderRadius: "8px",
                margin: "2px 0",
                minHeight: "44px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <ListItemIcon sx={{ minWidth: 0 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  "& .MuiTypography-root": {
                    fontWeight: location.pathname === item.path ? 600 : 500,
                    fontSize: "0.875rem",
                    color:
                      location.pathname === item.path
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={() => {
            removeToken();
            history.push("/login");
            navigate("/login");
          }}
          sx={{
            borderColor: theme.palette.divider,
            color: theme.palette.error.main,
            "&:hover": {
              borderColor: theme.palette.error.main,
              backgroundColor: alpha(theme.palette.error.main, 0.06),
            },
          }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <QueryClientProvider client={queryClient}>
      {" "}
      {/* Wrap everything with QueryClientProvider */}
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* AppBar for Mobile */}
        {isMobile && (
          <AppBar
            position="fixed"
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1.5 }}
              >
                <MenuIcon />
              </IconButton>
              <Box
                component="img"
                src={logo}
                alt="Amoozical"
                sx={{ width: 28, height: 28, borderRadius: "8px", mr: 1.5 }}
              />
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{ fontWeight: 700, flexGrow: 1 }}
              >
                Amoozical Backoffice
              </Typography>
              <IconButton onClick={toggleMode} size="small">
                {mode === "dark" ? (
                  <LightModeOutlinedIcon fontSize="small" />
                ) : (
                  <DarkModeOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Toolbar>
          </AppBar>
        )}

        {/* Drawer */}
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // Without this the flex item grows to fit wide tables and the whole page scrolls sideways.
            minWidth: 0,
            p: { xs: 2, sm: 3 },
            backgroundColor: "background.default",
            minHeight: "100vh",
          }}
        >
          <Toolbar />
          <Routes>
            <Route path="/" element={<DashboardSection />} />
            <Route path="user" element={<UserSection />} />
            <Route path="user-tree" element={<UserTreeSection />} />
            <Route path="user-tree/:userId" element={<UserTreeSection />} />
            <Route path="bd" element={<BDSection />} />
            <Route path="bd/:bdId" element={<BDDetailsSection />} />
            <Route path="referrals" element={<ReferralsSection />} />
            <Route
              path="referrals/:referralId"
              element={<ReferralDetailsSection />}
            />
            <Route path="campaign-stats" element={<CampaignStatsSection />} />
            <Route path="courses" element={<CoursesSection />} />
            <Route path="course-links" element={<CourseLinkGeneratorSection />} />
            <Route
              path="courses/:courseId"
              element={<CourseSeasonsSection />}
            />
            <Route
              path="courses/:courseId/seasons/:seasonId"
              element={<CourseItemsSection />}
            />
            <Route path="live-stream" element={<LiveStreamSection />} />
            <Route
              path="miniapp-notifications"
              element={<MiniAppNotificationsSection />}
            />
            <Route
              path="bot-notifications"
              element={<BotNotificationsSection />}
            />
          </Routes>
        </Box>
      </Box>
    </QueryClientProvider>
  );
}
